'use client';

import type { ReactNode } from 'react';

import { CreatorInfo } from '@ps/types/creator';

import type { ResolveSubscribeExpressIntent } from '../../types/SubscribeExpressIntent';
import { SubscribeContext } from './context';

interface SubscribeProviderProps {
  creatorInfo: CreatorInfo;
  title?: string;
  onClose: () => void;
  onFollowed?: () => void;
  /** PDP-only post-success redirect for the paid-tier checkout. */
  successRedirectUrl?: string;
  /** PDP-only: soft-navigate to `/checkout` instead of a hard document nav. */
  softCheckoutNav?: boolean;
  /** Bio subscribe route: soft `router.replace` to checkout, dialog self-closes
   *  via pathname (tipping-style). See {@link SubscribeContextValue.routeCheckoutNav}. */
  routeCheckoutNav?: boolean;
  /** Bio route only: resolves the express Apple Pay subscription intent (server action). */
  resolveExpressIntent?: ResolveSubscribeExpressIntent;
  children: ReactNode;
}

/**
 * Holds the identity + close/followed callbacks the subscribe picker needs, so
 * the modal component and its cells don't thread them through props. The
 * selection / consent state lives in the picker component (local), not here —
 * this only carries what both entry points (bio CTA, account) supply.
 */
export function SubscribeProvider({
  creatorInfo,
  title = 'Subscribe to Support Me',
  onClose,
  onFollowed,
  successRedirectUrl,
  softCheckoutNav,
  routeCheckoutNav,
  resolveExpressIntent,
  children,
}: SubscribeProviderProps) {
  return (
    <SubscribeContext.Provider
      value={{
        creatorInfo,
        title,
        onClose,
        onFollowed,
        successRedirectUrl,
        softCheckoutNav,
        routeCheckoutNav,
        resolveExpressIntent,
      }}
    >
      {children}
    </SubscribeContext.Provider>
  );
}
